export const WEB_SPEECH_PROVIDER = "webspeech";

const DEFAULT_VOICE_TIMEOUT_MS = 1000;
const MAX_CHUNK_LENGTH = 180;

let activeSpeechToken = 0;

export function isWebSpeechSupported() {
    return typeof window !== "undefined" &&
        "speechSynthesis" in window &&
        "SpeechSynthesisUtterance" in window;
}

export async function getWebSpeechVoices({ timeoutMs = DEFAULT_VOICE_TIMEOUT_MS } = {}) {
    if (!isWebSpeechSupported()) {
        return [];
    }

    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
        return voices;
    }

    return new Promise((resolve) => {
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            window.speechSynthesis.onvoiceschanged = null;
            resolve(window.speechSynthesis.getVoices());
        };

        window.speechSynthesis.onvoiceschanged = finish;
        window.setTimeout(finish, timeoutMs);
    });
}

export async function getWebSpeechVoiceCatalog() {
    const voices = await getWebSpeechVoices();
    return voices.reduce(
        (catalog, voice) => {
            const entry = toVoiceCatalogEntry(voice);
            catalog[entry.gender === "female" ? "female" : "male"].push(entry);
            return catalog;
        },
        { male: [], female: [] },
    );
}

export function toVoiceCatalogEntry(voice) {
    const gender = inferVoiceGender(voice?.name || "");
    return {
        provider: WEB_SPEECH_PROVIDER,
        id: voice?.voiceURI || voice?.name || "",
        name: voice?.name || "System Voice",
        voiceURI: voice?.voiceURI || voice?.name || "",
        styles: ["default"],
        gender,
        language: voice?.lang || "und",
        localService: Boolean(voice?.localService),
        default: Boolean(voice?.default),
    };
}

export function isLowQualityVoice(voice) {
    const name = (voice?.name || "").toLowerCase();
    return Boolean(
        voice?.localService &&
            /microsoft (david|zira|mark|hazel)|desktop|sapi|legacy/.test(name),
    );
}

export function isNetworkDependentVoice(voice) {
    return voice?.localService === false;
}

export async function speak(text, { voiceURI, rate = 1, pitch = 1, volume = 1 } = {}) {
    if (!isWebSpeechSupported()) {
        throw new Error("Web Speech API is not available in this browser.");
    }

    const cleanText = String(text || "").trim();
    if (!cleanText) {
        return;
    }

    cancel();
    const token = activeSpeechToken;
    const voice = await findVoice(voiceURI);
    const chunks = splitIntoSpeechChunks(cleanText);

    for (const chunk of chunks) {
        if (token !== activeSpeechToken) return;
        await speakChunk(chunk, {
            voice,
            rate: clamp(rate, 0.1, 10),
            pitch: clamp(pitch, 0, 2),
            volume: clamp(volume, 0, 1),
            token,
        });
    }
}

export function cancel() {
    activeSpeechToken += 1;
    if (isWebSpeechSupported()) {
        window.speechSynthesis.cancel();
    }
}

async function findVoice(voiceURI) {
    if (!voiceURI) return null;
    const voices = await getWebSpeechVoices();
    return voices.find((voice) => voice.voiceURI === voiceURI || voice.name === voiceURI) || null;
}

function speakChunk(text, { voice, rate, pitch, volume, token }) {
    return new Promise((resolve, reject) => {
        const utterance = new window.SpeechSynthesisUtterance(text);
        if (voice) utterance.voice = voice;
        utterance.rate = rate;
        utterance.pitch = pitch;
        utterance.volume = volume;

        utterance.onend = () => resolve();
        utterance.onerror = (event) => {
            if (token !== activeSpeechToken || event.error === "interrupted" || event.error === "canceled") {
                resolve();
                return;
            }
            console.log("Speech synthesis failed.");
            console.log(event);
            reject(new Error(event.error || "Speech synthesis failed."));
        };

        window.speechSynthesis.speak(utterance);
    });
}

function splitIntoSpeechChunks(text) {
    const chunks = [];
    let remaining = text.replace(/\s+/g, " ");

    while (remaining.length > MAX_CHUNK_LENGTH) {
        const breakIndex = findBreakIndex(remaining);
        chunks.push(remaining.slice(0, breakIndex).trim());
        remaining = remaining.slice(breakIndex).trim();
    }

    if (remaining) {
        chunks.push(remaining);
    }

    return chunks;
}

function findBreakIndex(text) {
    const sentenceBreak = Math.max(
        text.lastIndexOf(". ", MAX_CHUNK_LENGTH),
        text.lastIndexOf("? ", MAX_CHUNK_LENGTH),
        text.lastIndexOf("! ", MAX_CHUNK_LENGTH),
    );
    if (sentenceBreak > MAX_CHUNK_LENGTH * 0.5) {
        return sentenceBreak + 1;
    }

    const wordBreak = text.lastIndexOf(" ", MAX_CHUNK_LENGTH);
    return wordBreak > 0 ? wordBreak : MAX_CHUNK_LENGTH;
}

function inferVoiceGender(name) {
    const lowerName = name.toLowerCase();
    if (/(zira|hazel|susan|samantha|victoria|karen|moira|tessa|fiona|female)/.test(lowerName)) {
        return "female";
    }
    return "male";
}

function clamp(value, min, max) {
    const number = Number(value);
    if (Number.isNaN(number)) return min;
    return Math.min(max, Math.max(min, number));
}
